<template>
  <div class="metrics-class-header">
    <div class="metrics-class-header__left">
      <div class="metrics-class-header__title-row">
        <eg-button
          class="metrics-class-header__back"
          type="ordinary"
          icon="arrow-left"
          icon-size="extra-medium"
          @click="$router.back()"
        />
        <h1 class="metrics-class-header__title">
          {{ title }}
          <span v-if="isDemoMode" class="metrics-class-header__demo-label">{{
            $t("metrics.demoLabel")
          }}</span>
        </h1>
      </div>

      <div v-if="hasChips" class="metrics-class-header__chips">
        <tag-item
          v-if="teachersLabel"
          v-b-tooltip.hover="teacherHintText"
          :label="teachersLabel"
          size="medium"
        >
          <eg-icon icon="users-group" class="metrics-class-header__chip-icon" />
        </tag-item>
        <tag-item v-if="teacher" :label="teacher" size="medium">
          <eg-icon icon="student" class="metrics-class-header__chip-icon" />
        </tag-item>
        <tag-item v-if="className" :label="className" size="medium">
          <eg-icon icon="class" class="metrics-class-header__chip-icon" />
        </tag-item>
        <tag-item
          v-if="gradedDate"
          :label="$t('metrics.gradedDate', { date: gradedDate })"
          size="medium"
        />
        <tag-item
          v-if="studentsCount != null"
          :label="$t('metrics.studentsTag', { count: studentsCount })"
          size="medium"
        />
        <tag-item
          v-if="assignmentsCount != null"
          :label="$t('metrics.basedOnAssignments', { count: assignmentsCount })"
          size="medium"
        />
      </div>
    </div>

    <div class="metrics-class-header__actions no-print">
      <eg-toggle
        v-if="!hideDemoToggle"
        :value="isDemoMode"
        :label="
          isDemoMode
            ? $t('metrics.switchToRealData')
            : $t('metrics.switchToDemoData')
        "
        class="metrics-class-header__demo-toggle"
        @input="$emit('update:isDemoMode', $event)"
      />
      <div class="metrics-class-header__action-group">
        <eg-button
          v-if="showRegenerate && !isDemoMode"
          type="secondary"
          icon="refresh-rotate"
          icon-size="medium"
          :disabled="isPageLoading"
          @click="$emit('regenerate')"
        >
          {{ $t("metrics.regenerate") }}
        </eg-button>
        <eg-dropdown
          right
          :disabled="isPageLoading"
          class="metrics-class-header__more-dropdown"
        >
          <template #button-content>
            <eg-icon icon="more-options-vertical-dots-dots" size="medium" />
          </template>
          <eg-b-dropdown-item @click="$emit('export-pdf')">
            <eg-icon icon="file-download" size="medium" />
            {{ $t("metrics.exportPdf") }}
          </eg-b-dropdown-item>
          <eg-b-dropdown-item @click="$emit('print')">
            <eg-icon icon="print" size="medium" />
            {{ $t("metrics.print") }}
          </eg-b-dropdown-item>
        </eg-dropdown>
      </div>
    </div>
  </div>
</template>

<script>
import TagItem from "@/components/folders/tag-item.vue";

export default {
  name: "MetricsClassHeader",

  components: {
    TagItem,
  },

  props: {
    title: {
      type: String,
      default: null,
    },
    isDemoMode: {
      type: Boolean,
      default: false,
    },
    teacher: {
      type: String,
      default: null,
    },
    studentsCount: {
      type: Number,
      default: null,
    },
    assignmentsCount: {
      type: Number,
      default: null,
    },
    className: {
      type: String,
      default: null,
    },
    gradedDate: {
      type: String,
      default: null,
    },
    hideDemoToggle: {
      type: Boolean,
      default: false,
    },
    isPageLoading: {
      type: Boolean,
      default: false,
    },
    showRegenerate: {
      type: Boolean,
      default: false,
    },
    teachersLabel: {
      type: String,
      default: null,
    },
    teacherHintAssignments: {
      type: Array,
      default: () => [],
    },
  },

  computed: {
    hasChips() {
      return (
        this.teachersLabel ||
        this.teacher ||
        this.className ||
        this.gradedDate ||
        this.studentsCount != null ||
        this.assignmentsCount != null
      );
    },

    teacherHintText() {
      if (!this.teacherHintAssignments.length) return null;
      return this.teacherHintAssignments.join("\n");
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../shared/metrics_card";

.metrics-class-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding: 16px 24px;
  @media (max-width: 1700px) {
    background: var(--color-surface-neutral-background, #fff);
    border-bottom: 1px solid var(--color-stroke-neutral-default, #e5ebf0);
  }

  &__left {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
    min-height: 64px;
    min-width: 0;
  }

  &__title-row {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__back {
    color: var(--color-text-neutral-on-white);
  }

  &__title {
    color: var(--color-text-neutral-on-white);
    font-family: var(--typography-font-family-text-primary, Inter);
    font-size: 24px;
    font-weight: var(--typography-font-weight-fw-700, 700);
    line-height: 120%;
    margin: 0;
  }

  &__demo-label {
    color: var(--color-text-neutral-fade);
    font-weight: var(--typography-font-weight-fw-500, 500);
  }

  &__demo-toggle {
    min-height: 20px;
    flex-shrink: 0;
  }

  &__chips {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    padding-left: 44px;
  }

  &__chip-icon {
    font-size: var(--typography-font-size-fs-400);
    color: var(--color-text-neutral-fade);
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
    @media (max-width: 1200px) {
      flex-direction: column;
      align-items: flex-end;
    }
  }

  &__action-group {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
  }

  ::v-deep {
    .eg-toggle {
      width: 190px;
      label {
        line-height: inherit;
        padding-left: 12px;
      }
    }
    .tag {
      .tag-title {
        line-height: inherit;
      }
      background-color: var(--color-surface-neutral-background);
    }
  }

  &__more-dropdown {
    @include metrics-dropdown-icon;
  }
}
</style>
